/**
 * Generates fake video streams for testing without physical cameras.
 * Creates various test patterns that can simulate different video content.
 */

export type TestPattern =
  | 'color-bars'      // Classic TV test pattern
  | 'grid'            // Grid pattern for resolution testing
  | 'gradient'        // Smooth gradient
  | 'moving-box'      // Moving element for frame rate testing
  | 'timestamp'       // Shows current timestamp
  | 'noise'           // Random noise
  | 'image';          // User-provided image

export interface FakeVideoOptions {
  width: number;
  height: number;
  frameRate: number;
  pattern: TestPattern;
  label?: string;
  image?: HTMLImageElement;
}

export class FakeVideoGenerator {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private stream: MediaStream | null = null;
  private animationId: number | null = null;
  private frameCount = 0;
  private options: FakeVideoOptions;

  constructor(options: Partial<FakeVideoOptions> = {}) {
    this.options = {
      width: 1920,
      height: 1080,
      frameRate: 30,
      pattern: 'color-bars',
      ...options,
    };

    this.canvas = document.createElement('canvas');
    this.canvas.width = this.options.width;
    this.canvas.height = this.options.height;

    const ctx = this.canvas.getContext('2d');
    if (!ctx) throw new Error('Failed to get canvas context');
    this.ctx = ctx;
  }

  start(): MediaStream {
    if (this.stream) return this.stream;

    // Create media stream from canvas
    this.stream = this.canvas.captureStream(this.options.frameRate);

    // Start rendering frames
    this.renderLoop();

    return this.stream;
  }

  stop(): void {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }

    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }
  }

  private renderLoop = (): void => {
    this.renderFrame();
    this.frameCount++;
    this.animationId = requestAnimationFrame(this.renderLoop);
  };

  private renderFrame(): void {
    const { width, height, pattern, label } = this.options;
    const ctx = this.ctx;

    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, width, height);

    switch (pattern) {
      case 'color-bars':
        this.drawColorBars();
        break;
      case 'grid':
        this.drawGrid();
        break;
      case 'gradient':
        this.drawGradient();
        break;
      case 'moving-box':
        this.drawMovingBox();
        break;
      case 'timestamp':
        this.drawTimestamp();
        break;
      case 'noise':
        this.drawNoise();
        break;
      case 'image':
        this.drawImage();
        break;
    }

    // Draw label if provided
    if (label) {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
      ctx.fillRect(10, height - 40, ctx.measureText(label).width + 20, 30);
      ctx.fillStyle = '#fff';
      ctx.font = '18px monospace';
      ctx.fillText(label, 20, height - 18);
    }

    // Always show resolution and frame count
    const info = `${width}x${height} | Frame: ${this.frameCount}`;
    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    ctx.fillRect(10, 10, ctx.measureText(info).width + 20, 30);
    ctx.fillStyle = '#0f0';
    ctx.font = '14px monospace';
    ctx.fillText(info, 20, 30);
  }

  private drawColorBars(): void {
    const { width, height } = this.options;
    const colors = ['#fff', '#ff0', '#0ff', '#0f0', '#f0f', '#f00', '#00f', '#000'];
    const barWidth = width / colors.length;

    colors.forEach((color, i) => {
      this.ctx.fillStyle = color;
      this.ctx.fillRect(i * barWidth, 0, barWidth, height * 0.7);
    });

    // Bottom section with grayscale
    const grayColors = ['#00214e', '#fff', '#32006f', '#000', '#090909', '#000', '#1d1d1d', '#000'];
    grayColors.forEach((color, i) => {
      this.ctx.fillStyle = color;
      this.ctx.fillRect(i * barWidth, height * 0.7, barWidth, height * 0.3);
    });
  }

  private drawGrid(): void {
    const { width, height } = this.options;
    const gridSize = 50;

    this.ctx.strokeStyle = '#444';
    this.ctx.lineWidth = 1;

    // Vertical lines
    for (let x = 0; x <= width; x += gridSize) {
      this.ctx.beginPath();
      this.ctx.moveTo(x, 0);
      this.ctx.lineTo(x, height);
      this.ctx.stroke();
    }

    // Horizontal lines
    for (let y = 0; y <= height; y += gridSize) {
      this.ctx.beginPath();
      this.ctx.moveTo(0, y);
      this.ctx.lineTo(width, y);
      this.ctx.stroke();
    }

    // Center crosshair
    this.ctx.strokeStyle = '#f00';
    this.ctx.lineWidth = 2;
    this.ctx.beginPath();
    this.ctx.moveTo(width / 2, 0);
    this.ctx.lineTo(width / 2, height);
    this.ctx.moveTo(0, height / 2);
    this.ctx.lineTo(width, height / 2);
    this.ctx.stroke();

    // Center circle
    this.ctx.beginPath();
    this.ctx.arc(width / 2, height / 2, Math.min(width, height) / 4, 0, Math.PI * 2);
    this.ctx.stroke();
  }

  private drawGradient(): void {
    const { width, height } = this.options;
    const gradient = this.ctx.createLinearGradient(0, 0, width, height);

    // Animate gradient colors
    const hue = (this.frameCount * 2) % 360;
    gradient.addColorStop(0, `hsl(${hue}, 70%, 50%)`);
    gradient.addColorStop(0.5, `hsl(${(hue + 120) % 360}, 70%, 50%)`);
    gradient.addColorStop(1, `hsl(${(hue + 240) % 360}, 70%, 50%)`);

    this.ctx.fillStyle = gradient;
    this.ctx.fillRect(0, 0, width, height);
  }

  private drawMovingBox(): void {
    const { width, height } = this.options;
    const boxSize = 100;
    const speed = 5;

    // Calculate position using sine waves for smooth movement
    const x = (width - boxSize) / 2 + Math.sin(this.frameCount * 0.05) * (width / 3);
    const y = (height - boxSize) / 2 + Math.cos(this.frameCount * 0.03) * (height / 3);

    // Background
    this.ctx.fillStyle = '#1a1a2e';
    this.ctx.fillRect(0, 0, width, height);

    // Trail effect
    for (let i = 10; i > 0; i--) {
      const trailX = (width - boxSize) / 2 + Math.sin((this.frameCount - i * 3) * 0.05) * (width / 3);
      const trailY = (height - boxSize) / 2 + Math.cos((this.frameCount - i * 3) * 0.03) * (height / 3);
      this.ctx.fillStyle = `rgba(99, 102, 241, ${0.1 * (10 - i) / 10})`;
      this.ctx.fillRect(trailX, trailY, boxSize, boxSize);
    }

    // Main box
    this.ctx.fillStyle = '#6366f1';
    this.ctx.fillRect(x, y, boxSize, boxSize);
  }

  private drawTimestamp(): void {
    const { width, height } = this.options;

    this.ctx.fillStyle = '#1a1a2e';
    this.ctx.fillRect(0, 0, width, height);

    const now = new Date();
    const timestamp = now.toISOString();
    const time = now.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3
    });

    // Large time display
    this.ctx.fillStyle = '#fff';
    this.ctx.font = `bold ${Math.floor(height / 6)}px monospace`;
    this.ctx.textAlign = 'center';
    this.ctx.fillText(time, width / 2, height / 2);

    // ISO timestamp below
    this.ctx.font = `${Math.floor(height / 20)}px monospace`;
    this.ctx.fillStyle = '#888';
    this.ctx.fillText(timestamp, width / 2, height / 2 + height / 8);

    this.ctx.textAlign = 'left';
  }

  private drawNoise(): void {
    const { width, height } = this.options;
    const imageData = this.ctx.createImageData(width, height);
    const data = imageData.data;

    for (let i = 0; i < data.length; i += 4) {
      const value = Math.random() * 255;
      data[i] = value;     // R
      data[i + 1] = value; // G
      data[i + 2] = value; // B
      data[i + 3] = 255;   // A
    }

    this.ctx.putImageData(imageData, 0, 0);
  }

  private drawImage(): void {
    const { width, height, image } = this.options;

    if (image && image.complete) {
      // Draw image centered and scaled to fit
      const scale = Math.min(width / image.width, height / image.height);
      const scaledWidth = image.width * scale;
      const scaledHeight = image.height * scale;
      const x = (width - scaledWidth) / 2;
      const y = (height - scaledHeight) / 2;

      this.ctx.fillStyle = '#000';
      this.ctx.fillRect(0, 0, width, height);
      this.ctx.drawImage(image, x, y, scaledWidth, scaledHeight);
    } else {
      // Fallback to color bars if no image
      this.drawColorBars();
    }
  }

  setImage(image: HTMLImageElement): void {
    this.options.image = image;
    this.options.pattern = 'image';
  }

  setPattern(pattern: TestPattern): void {
    this.options.pattern = pattern;
  }

  getCanvas(): HTMLCanvasElement {
    return this.canvas;
  }
}

/**
 * Creates a fake video stream at a specific resolution
 */
export function createFakeVideoStream(
  resolution: '360p' | '720p' | '1080p' | '4k',
  pattern: TestPattern = 'color-bars',
  label?: string
): MediaStream {
  const resolutions = {
    '360p': { width: 640, height: 360 },
    '720p': { width: 1280, height: 720 },
    '1080p': { width: 1920, height: 1080 },
    '4k': { width: 3840, height: 2160 },
  };

  const { width, height } = resolutions[resolution];
  const generator = new FakeVideoGenerator({
    width,
    height,
    frameRate: resolution === '4k' ? 5 : 30, // Lower FPS for 4K
    pattern,
    label: label || `${resolution} Test`,
  });

  return generator.start();
}
